import { FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Cryptocurrency } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { CryptoIcon } from '@sorare/core/src/atoms/icons/CryptoIcon';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useCurrentUserCurrencies } from '@sorare/core/src/hooks/useCurrentUserCurrencies';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { faFiatCurrencySymbols } from '@sorare/core/src/lib/fiat';
import { SOL_DECIMAL_PLACES } from '@sorare/core/src/lib/lamport';
import type { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';
import { ETH_DECIMAL_PLACES, RoundingMode } from '@sorare/core/src/lib/wei';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--intermediate-unit);
`;
const Group = styled(Horizontal)`
  background: var(--c-nd-100);
  border-radius: var(--intermediate-unit);
  padding: var(--intermediate-unit);
`;

type Props = {
  wei: bigint;
  lamport: bigint;
  fiat: MonetaryAmount;
};

export const MonetaryRewards = ({ wei, fiat, lamport }: Props) => {
  const { formatWei, formatLamport } = useIntlContext();
  const { getUserFiatAmountInMajorUnit } = useMonetaryAmount();
  const { fiatCurrency } = useCurrentUserCurrencies();
  const faFiatCurrencySymbol = faFiatCurrencySymbols[fiatCurrency];
  const amount = getUserFiatAmountInMajorUnit(fiat);

  return (
    <Grid>
      <Group>
        <FontAwesomeIcon icon={faFiatCurrencySymbol} />
        <BodyS>
          <FormattedNumber
            value={amount}
            style="currency"
            currency={fiatCurrency}
          />
        </BodyS>
      </Group>
      {wei > 0n && (
        <Group>
          <CryptoIcon cryptocurrency={Cryptocurrency.WEI} />
          <BodyS>
            {formatWei(
              wei,
              RoundingMode.ROUND_DOWN,
              {
                maximumFractionDigits: ETH_DECIMAL_PLACES,
              },
              { hideSymbol: true }
            )}
          </BodyS>
        </Group>
      )}
      {lamport > 0n && (
        <Group>
          <CryptoIcon cryptocurrency={Cryptocurrency.LAMPORT} />
          <BodyS>
            {formatLamport(
              lamport,
              RoundingMode.ROUND_DOWN,
              {
                maximumFractionDigits: SOL_DECIMAL_PLACES,
              },
              { hideSymbol: true }
            )}
          </BodyS>
        </Group>
      )}
    </Grid>
  );
};
